import { useRef, useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/errors';
import { mapFieldErrors } from '@/api/fieldErrors';
import type { PromotionView } from '@/api/generated';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { ApiErrorState } from '@/components/ApiErrorState';
import { SubmitButton } from '@/components/SubmitButton';
import { useActionError, type ActionError } from '@/components/useActionError';
import { useFieldErrors } from '@/components/useFieldErrors';
import { useFreshOnOpen } from '@/components/useFreshOnOpen';
import { useTenantApi } from '@/tenant/useTenant';
import { useToast } from '@/components/useToast';
import { applyPromotionErrorMessage } from './subscriberErrors';

export interface ApplyPromotionDialogProps {
  open: boolean;
  onClose: () => void;
  onDone: (opts?: { quiet?: boolean }) => void;
  subscriptionId: number;
  promotions: PromotionView[];
  loadError?: unknown;
  onRetryLoad?: () => void;
}

const FIELDS = ['code'] as const;

export function ApplyPromotionDialog(props: ApplyPromotionDialogProps) {
  // A fresh form on every open (TMMRSL-0071).
  return <ApplyPromotionForm key={useFreshOnOpen(props.open)} {...props} />;
}

function ApplyPromotionForm({
  open,
  onClose,
  onDone,
  subscriptionId,
  promotions,
  loadError = null,
  onRetryLoad,
}: ApplyPromotionDialogProps) {
  const { t } = useTranslation();
  const showToast = useToast();
  const api = useTenantApi();
  const describe = useActionError(applyPromotionErrorMessage);
  const codeRef = useRef<HTMLInputElement>(null);
  const active = promotions.filter((promo) => promo.status === 'ACTIVE' && promo.code);
  // The first active code as the dialog opens (it mounts fresh on each open); a pick that a refresh
  // drops is nothing.
  const [picked, setPicked] = useState(active[0]?.code ?? '');
  const code = active.some((promo) => promo.code === picked) ? picked : '';
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ActionError | null>(null);
  const fields = useFieldErrors(FIELDS, { code: codeRef }, submitting);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!code) {
      setError(null);
      fields.fail({ code: t('validation.chooseCode') });
      return;
    }
    setSubmitting(true);
    setError(null);
    fields.reset();
    try {
      await api.applyPromotion(subscriptionId, { code });
      showToast(t('subscribers.toast.promoApplied'));
      onDone();
      onClose();
    } catch (err) {
      const action = describe(err);
      setError(action);
      // An OFFER code moves the subscription to its offer before the redemption is recorded, so a
      // lost race answers 409 after the move: reload the detail whatever the 409 says.
      if (err instanceof ApiError && err.status === 409) onDone({ quiet: true });
      fields.failMarked(mapFieldErrors(action.fieldKeys, { code: 'code' }), {
        code: t('subscribers.field.code'),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose}>
      <Box component="form" noValidate onSubmit={(e) => void handleSubmit(e)}>
        <DialogTitle>{t('subscribers.applyPromo.title')}</DialogTitle>
        <DialogContent>
          {loadError != null && onRetryLoad ? (
            <ApiErrorState error={loadError} onRetry={onRetryLoad} />
          ) : null}
          <ActionErrorAlert error={error} sx={{ mb: 2 }} />
          <TextField
            select
            required
            fullWidth
            margin="dense"
            label={t('subscribers.applyPromo.code')}
            value={code}
            onChange={(e) => {
              setPicked(e.target.value);
              fields.clear('code');
            }}
            error={fields.errors.code !== undefined}
            helperText={fields.errors.code}
            inputRef={codeRef}
            disabled={submitting}
          >
            {active.map((promo) => (
              <MenuItem key={promo.code} value={promo.code}>
                {promo.code}
                {promo.name ? ` — ${promo.name}` : ''}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" loading={submitting}>
            {t('subscribers.actions.applyPromo')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
